import { DraftCredit } from "../components/CreditAccount";
import { InvoiceLinesEditor } from "../components/InvoiceLinesEditor";
import type { DraftLineInput, DraftQuoteView } from "@vereinfach/types";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { AuditEntry, CoverageChip, InvoiceDetail, Payment } from "../lib/types";
import { api, apiUrl, errorMessage, mutate } from "../lib/api";
import {
  PAYMENT_METHOD_LABELS,
  formatCHF,
  formatDate,
  formatIsoDate,
  formatPeriodRange,
  todayIso,
} from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { Modal } from "../components/Modal";
import { IssueModal, PaymentFormModal, parseChfToRappen, rappenToChfInput } from "./Invoices";

function isoPlusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

// --- Page --------------------------------------------------------------------

export function InvoiceDetailPage({
  tenantId,
  invoiceId,
}: {
  tenantId: string;
  invoiceId: string;
}) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  async function reload() {
    try {
      const d = await api<InvoiceDetail>(`/tenants/${tenantId}/invoices/${invoiceId}`);
      setDetail(d);
      setLoadError(null);
    } catch (e) {
      setLoadError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    setDetail(null);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, invoiceId]);

  const backLink = (
    <Link to="/invoices" className="text-sm text-slate-500 hover:text-slate-900">
      ‹ Rechnungen
    </Link>
  );

  if (loading)
    return (
      <div>
        {backLink}
        <p className="mt-4 text-slate-400">Lädt…</p>
      </div>
    );
  if (loadError || !detail)
    return (
      <div>
        {backLink}
        <p className="mt-4 text-sm text-red-600">{loadError ?? "Rechnung nicht gefunden."}</p>
      </div>
    );

  const inv = detail.invoice;
  const isDraft = inv.display_status === "entwurf";
  const title = isDraft
    ? "Entwurf"
    : inv.kind === "storno"
      ? `Stornorechnung ${inv.number ?? ""}`
      : `Rechnung ${inv.number ?? ""}`;

  return (
    <div>
      {backLink}
      <div className="mb-6 mt-2 flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        <StatusBadge status={inv.display_status} />
        <span className="text-sm text-slate-500">{inv.recipient_name}</span>
      </div>

      {isDraft ? (
        <DraftEditor
          key={`${inv.id}:${inv.updated_at}`}
          tenantId={tenantId}
          detail={detail}
          onSaved={setDetail}
          onReload={reload}
          onDeleted={() => navigate({ to: "/invoices" })}
        />
      ) : (
        <IssuedView
          tenantId={tenantId}
          detail={detail}
          onReload={reload}
          onNavigate={(id) => navigate({ to: "/invoices/$invoiceId", params: { invoiceId: id } })}
        />
      )}

      <AuditSection tenantId={tenantId} invoiceId={inv.id} />
    </div>
  );
}

// --- Draft worksheet -------------------------------------------------------------

function DraftEditor({
  tenantId,
  detail,
  onSaved,
  onReload,
  onDeleted,
}: {
  tenantId: string;
  detail: InvoiceDetail;
  onSaved: (d: InvoiceDetail) => void;
  onReload: () => void;
  onDeleted: () => void;
}) {
  const inv = detail.invoice;
  const [name, setName] = useState(inv.recipient_name);
  const [line1, setLine1] = useState(inv.recipient_line1 ?? "");
  const [line2, setLine2] = useState(inv.recipient_line2 ?? "");
  const [zip, setZip] = useState(inv.recipient_zip ?? "");
  const [city, setCity] = useState(inv.recipient_city ?? "");
  const [country, setCountry] = useState(inv.recipient_country || "CH");
  const [invoiceDate, setInvoiceDate] = useState(inv.invoice_date);
  const [dueDate, setDueDate] = useState(inv.due_date ?? "");
  const [headerText, setHeaderText] = useState(inv.header_text ?? "");
  const [footerText, setFooterText] = useState(inv.footer_text ?? "");
  const [lineInputs, setLineInputs] = useState<DraftLineInput[]>(
    detail.lines.map((l) => ({
      description: l.description,
      amount_rappen: l.amount_rappen,
      definition: l.definition,
    })),
  );
  const [quote, setQuote] = useState<DraftQuoteView | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showIssue, setShowIssue] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [detaching, setDetaching] = useState<CoverageChip | null>(null);

  function touch<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setDirty(true);
    };
  }

  const linesValid = quote !== null;

  async function save(): Promise<InvoiceDetail | null> {
    if (!name.trim()) {
      setFormError("Empfängername fehlt.");
      return null;
    }
    if (!invoiceDate) {
      setFormError("Rechnungsdatum fehlt.");
      return null;
    }
    if (!linesValid) {
      setFormError("Bitte alle Positionen mit Beschreibung und gültigem Betrag ausfüllen.");
      return null;
    }
    setSaving(true);
    setFormError(null);
    try {
      const updated = await mutate<InvoiceDetail>(
        `/tenants/${tenantId}/invoices/${inv.id}`,
        {
          recipient: {
            name: name.trim(),
            line1: line1.trim() || null,
            line2: line2.trim() || null,
            zip: zip.trim() || null,
            city: city.trim() || null,
            country: country.trim().toUpperCase() || "CH",
          },
          invoice_date: invoiceDate,
          ...(dueDate ? { due_date: dueDate } : {}),
          header_text: headerText.trim() || null,
          footer_text: footerText.trim() || null,
          lines: lineInputs,
          expected_revision: quote?.revision,
        },
        "PATCH",
      );
      onSaved(updated);
      return updated;
    } catch (e) {
      setFormError(errorMessage(e));
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function handleIssue() {
    if (dirty) {
      const saved = await save();
      if (!saved) return;
    }
    setShowIssue(true);
  }

  async function handlePreview() {
    if (dirty) {
      const saved = await save();
      if (!saved) return;
    }
    window.open(
      apiUrl(`/tenants/${tenantId}/invoices/${inv.id}/pdf?variant=auto`),
      "_blank",
      "noopener,noreferrer",
    );
  }

  async function handleDelete() {
    if (inv.coverage.length > 0) {
      setShowDelete(true);
      return;
    }
    if (!window.confirm("Diesen Entwurf endgültig löschen?")) return;
    try {
      await mutate(`/tenants/${tenantId}/invoices/${inv.id}`, {}, "DELETE");
      onDeleted();
    } catch (e) {
      setFormError(errorMessage(e));
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Empfänger">
          <div className="grid grid-cols-1 gap-3">
            <Field label="Name">
              <input
                value={name}
                onChange={(e) => touch(setName)(e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Strasse">
              <input
                value={line1}
                onChange={(e) => touch(setLine1)(e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Zusatz">
              <input
                value={line2}
                onChange={(e) => touch(setLine2)(e.target.value)}
                className="input"
              />
            </Field>
            <div className="grid grid-cols-4 gap-3">
              <Field label="PLZ">
                <input
                  value={zip}
                  onChange={(e) => touch(setZip)(e.target.value)}
                  className="input"
                />
              </Field>
              <div className="col-span-2">
                <Field label="Ort">
                  <input
                    value={city}
                    onChange={(e) => touch(setCity)(e.target.value)}
                    className="input"
                  />
                </Field>
              </div>
              <Field label="Land">
                <input
                  value={country}
                  onChange={(e) => touch(setCountry)(e.target.value)}
                  maxLength={2}
                  className="input"
                />
              </Field>
            </div>
          </div>
        </Card>

        <Card title="Daten">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Rechnungsdatum">
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => touch(setInvoiceDate)(e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Fällig am">
              <input
                type="date"
                value={dueDate}
                onChange={(e) => touch(setDueDate)(e.target.value)}
                className="input"
              />
            </Field>
          </div>
          {inv.coverage.length > 0 && (
            <div className="mt-4">
              <span className="text-xs font-medium text-slate-500">Deckt ab</span>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {inv.coverage.map((c) => (
                  <span
                    key={`${c.subscription_id}:${c.period_start}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
                    title={c.package_name ?? undefined}
                  >
                    {c.member_name ? `${c.member_name} · ` : ""}
                    {formatPeriodRange(c.period_start, c.period_end)}
                    <button
                      onClick={() => setDetaching(c)}
                      className="text-slate-400 hover:text-red-600"
                      aria-label="Periode entfernen"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      <Card title="Positionen">
        <InvoiceLinesEditor
          tenantId={tenantId}
          invoiceId={inv.id}
          memberId={inv.member_id}
          refreshToken={inv.credit_requested_rappen ?? 0}
          value={lineInputs}
          onChange={(next) => {
            setLineInputs(next);
            setDirty(true);
          }}
          onQuote={setQuote}
        />
      </Card>

      {inv.member_id && (
        <DraftCredit
          tenantId={tenantId}
          invoiceId={inv.id}
          memberId={inv.member_id}
          requested={inv.credit_requested_rappen ?? 0}
          onSaved={onReload}
        />
      )}
      <Card title="Texte">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <Field label="Text oben (vor den Positionen)">
            <textarea
              value={headerText}
              onChange={(e) => touch(setHeaderText)(e.target.value)}
              rows={3}
              className="input"
            />
          </Field>
          <Field label="Text unten (nach den Positionen)">
            <textarea
              value={footerText}
              onChange={(e) => touch(setFooterText)(e.target.value)}
              rows={3}
              className="input"
            />
          </Field>
        </div>
      </Card>

      {formError && <p className="text-sm text-red-600">{formError}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={handleIssue}
          disabled={saving}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          Freigeben
        </button>
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
        >
          {saving ? "Speichern…" : dirty ? "Speichern" : "Gespeichert"}
        </button>
        <button
          onClick={handlePreview}
          title="Vorschau mit ENTWURF-Wasserzeichen"
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium hover:bg-slate-50"
        >
          Vorschau (PDF)
        </button>
        <div className="flex-1" />
        <button
          onClick={handleDelete}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
        >
          Löschen
        </button>
      </div>

      {showIssue && (
        <IssueModal
          tenantId={tenantId}
          invoice={inv}
          onClose={() => setShowIssue(false)}
          onIssued={() => {
            setShowIssue(false);
            onReload();
          }}
        />
      )}

      {detaching && (
        <PeriodChoiceModal
          title="Periode entfernen"
          intro={`${detaching.member_name ?? "Periode"} · ${formatPeriodRange(
            detaching.period_start,
            detaching.period_end,
          )} aus dieser Rechnung entfernen. Was soll mit der Periode passieren?`}
          onClose={() => setDetaching(null)}
          onChoose={async (skip) => {
            await mutate(`/tenants/${tenantId}/invoices/${inv.id}/coverage/detach`, {
              subscription_id: detaching.subscription_id,
              period_start: detaching.period_start,
              skip_period: skip,
            });
            setDetaching(null);
            onReload();
          }}
        />
      )}

      {showDelete && (
        <PeriodChoiceModal
          title="Entwurf löschen"
          intro="Dieser Entwurf deckt Beitragsperioden ab. Was soll mit den Perioden passieren?"
          onClose={() => setShowDelete(false)}
          onChoose={async (skip) => {
            // Skip ("do not re-bill") rides in the query so it can't be dropped.
            await mutate(
              `/tenants/${tenantId}/invoices/${inv.id}${skip ? "?skip=1" : ""}`,
              {},
              "DELETE",
            );
            onDeleted();
          }}
        />
      )}
    </div>
  );
}

/** Two-way choice: skip the period vs. let it re-bill later. */
function PeriodChoiceModal({
  title,
  intro,
  onClose,
  onChoose,
}: {
  title: string;
  intro: string;
  onClose: () => void;
  onChoose: (skipPeriod: boolean) => Promise<void>;
}) {
  const [choice, setChoice] = useState<"skip" | "rebill">("rebill");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal title={title} onClose={onClose}>
      <p className="text-sm text-slate-600">{intro}</p>
      <div className="mt-4 space-y-2 text-sm">
        <label className="flex items-start gap-2">
          <input
            type="radio"
            checked={choice === "rebill"}
            onChange={() => setChoice("rebill")}
            className="mt-0.5 accent-slate-900"
          />
          <span>
            <span className="font-medium">Später erneut verrechnen</span>
            <span className="block text-xs text-slate-500">
              Beim nächsten Rechnungslauf entsteht wieder ein Entwurf für diese Periode.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2">
          <input
            type="radio"
            checked={choice === "skip"}
            onChange={() => setChoice("skip")}
            className="mt-0.5 accent-slate-900"
          />
          <span>
            <span className="font-medium">Periode überspringen</span>
            <span className="block text-xs text-slate-500">
              Es wird keine neue Rechnung für diese Periode erstellt.
            </span>
          </span>
        </label>
      </div>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="btn-sm px-3 py-1.5 text-sm">
          Abbrechen
        </button>
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await onChoose(choice === "skip");
            } catch (e) {
              setError(errorMessage(e));
              setBusy(false);
            }
          }}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? "Bitte warten…" : "Bestätigen"}
        </button>
      </div>
    </Modal>
  );
}

// --- Issued (read-only) view -----------------------------------------------------

function IssuedView({
  tenantId,
  detail,
  onReload,
  onNavigate,
}: {
  tenantId: string;
  detail: InvoiceDetail;
  onReload: () => void;
  onNavigate: (invoiceId: string) => void;
}) {
  const inv = detail.invoice;
  const isCancelled = inv.cancelled_at != null;
  const isStorno = inv.kind === "storno";
  const [actionError, setActionError] = useState<string | null>(null);

  const [addingPayment, setAddingPayment] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [showReminder, setShowReminder] = useState(false);
  const [showMahnpause, setShowMahnpause] = useState(false);
  const [showWriteOff, setShowWriteOff] = useState(false);
  const [showReduce, setShowReduce] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  function openPdf(variant: "auto" | "original") {
    window.open(
      apiUrl(`/tenants/${tenantId}/invoices/${inv.id}/pdf?variant=${variant}`),
      "_blank",
      "noopener,noreferrer",
    );
  }

  async function run(fn: () => Promise<void>) {
    try {
      await fn();
      setActionError(null);
    } catch (e) {
      setActionError(errorMessage(e));
    }
  }

  async function markSent(via: "post") {
    await mutate(`/tenants/${tenantId}/invoices/${inv.id}/mark-sent`, { via });
    onReload();
  }

  async function deletePayment(p: Payment) {
    if (!window.confirm(`Zahlung über ${formatCHF(p.amount_rappen)} löschen?`)) return;
    await run(async () => {
      await mutate(`/tenants/${tenantId}/payments/${p.id}`, {}, "DELETE");
      onReload();
    });
  }

  async function reassignAllPayments() {
    if (!detail.replaced_by) return;
    const target = detail.replaced_by;
    await run(async () => {
      for (const p of detail.payments.filter((payment) => payment.method !== "credit")) {
        await mutate(`/tenants/${tenantId}/payments/${p.id}/reassign`, {
          to_invoice_id: target.id,
        });
      }
      onReload();
    });
  }

  const canRemind = !isStorno && !isCancelled && inv.balance_rappen > 0;
  const canCorrect = !isStorno && !isCancelled;

  return (
    <div className="space-y-4">
      {isCancelled && (
        <Banner tone="red">
          Storniert am {formatDate(inv.cancelled_at as number)}
          {detail.reversed_by && (
            <>
              {" — Stornorechnung "}
              <ChainLink target={detail.reversed_by} onNavigate={onNavigate} />
            </>
          )}
        </Banner>
      )}
      {isStorno && detail.reverses && (
        <Banner tone="violet">
          Storno zu Rechnung <ChainLink target={detail.reverses} onNavigate={onNavigate} />
        </Banner>
      )}
      {detail.replaced_by && (
        <Banner tone="slate">
          Ersetzt durch <ChainLink target={detail.replaced_by} onNavigate={onNavigate} />
        </Banner>
      )}
      {detail.replaces && (
        <Banner tone="slate">
          Ersetzt Rechnung <ChainLink target={detail.replaces} onNavigate={onNavigate} />
        </Banner>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap justify-between gap-6">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Empfänger
            </div>
            <div className="mt-1 text-sm text-slate-900">
              <div className="font-medium">{inv.recipient_name}</div>
              {inv.recipient_line1 && <div>{inv.recipient_line1}</div>}
              {inv.recipient_line2 && <div>{inv.recipient_line2}</div>}
              {(inv.recipient_zip || inv.recipient_city) && (
                <div>
                  {inv.recipient_zip} {inv.recipient_city}
                </div>
              )}
            </div>
          </div>
          <div className="text-sm">
            <div className="flex gap-6">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Rechnungsdatum
                </div>
                <div className="mt-1">{formatIsoDate(inv.invoice_date)}</div>
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Fällig am
                </div>
                <div className="mt-1">{inv.due_date ? formatIsoDate(inv.due_date) : "—"}</div>
              </div>
            </div>
            <div className="mt-3 space-y-0.5 text-xs text-slate-500">
              {inv.sent_at != null && (
                <div>
                  Versendet per {inv.sent_via === "email" ? "E-Mail" : "Post"} am{" "}
                  {formatDate(inv.sent_at)}
                </div>
              )}
              {inv.reminder_count > 0 && inv.last_reminder_at != null && (
                <div>
                  {inv.reminder_count}. Erinnerung am {formatDate(inv.last_reminder_at)} (
                  {Array.from({ length: inv.reminder_count }, (_, i) => i + 1).map((n, i) => (
                    <span key={n}>
                      {i > 0 && ", "}
                      <a
                        href={apiUrl(`/tenants/${tenantId}/invoices/${inv.id}/reminder/${n}/pdf`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:no-underline"
                      >
                        PDF {n}
                      </a>
                    </span>
                  ))}
                  )
                </div>
              )}
              {inv.mahnpause_until && (
                <div>Zahlungspause bis {formatIsoDate(inv.mahnpause_until)}</div>
              )}
              {inv.written_off_at != null && (
                <div>
                  Abgeschrieben am {formatDate(inv.written_off_at)}
                  {inv.write_off_note ? ` — ${inv.write_off_note}` : ""}
                </div>
              )}
            </div>
          </div>
        </div>

        {inv.coverage.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
            <span>Deckt ab:</span>
            {inv.coverage.map((c) => (
              <span
                key={`${c.subscription_id}:${c.period_start}`}
                className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-slate-600"
                title={c.package_name ?? undefined}
              >
                {c.member_name ? `${c.member_name} · ` : ""}
                {formatPeriodRange(c.period_start, c.period_end)}
              </span>
            ))}
          </div>
        )}

        {inv.header_text && (
          <p className="mt-4 whitespace-pre-line text-sm text-slate-600">{inv.header_text}</p>
        )}

        <table className="mt-4 w-full text-sm">
          <thead className="text-slate-500">
            <tr className="border-b border-slate-100">
              <th className="py-2 text-left font-medium">Beschreibung</th>
              <th className="py-2 text-right font-medium">Betrag</th>
            </tr>
          </thead>
          <tbody>
            {detail.lines.map((l) => (
              <tr key={l.id} className="border-b border-slate-50">
                <td className="py-2">{l.description}</td>
                <td className="whitespace-nowrap py-2 text-right">{formatCHF(l.amount_rappen)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="py-2 font-semibold">Total</td>
              <td className="whitespace-nowrap py-2 text-right font-semibold">
                {formatCHF(inv.total_rappen)}
              </td>
            </tr>
          </tfoot>
        </table>

        {inv.footer_text && (
          <p className="mt-2 whitespace-pre-line text-sm text-slate-600">{inv.footer_text}</p>
        )}
      </div>

      {actionError && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-700">
            ✕
          </button>
        </div>
      )}

      {!isStorno && (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Zahlungen</h3>
            <button onClick={() => setAddingPayment(true)} className="btn-sm">
              Zahlung erfassen
            </button>
          </div>

          {isCancelled && inv.paid_rappen !== 0 && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <p className="font-medium">
                Zahlung {formatCHF(inv.paid_rappen)} auf stornierter Rechnung.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {detail.replaced_by && (
                  <button
                    onClick={reassignAllPayments}
                    className="rounded-lg bg-amber-700 px-3 py-1 text-xs font-medium text-white hover:bg-amber-800"
                  >
                    Der Ersatzrechnung zuordnen
                  </button>
                )}
                <span className="text-xs">
                  Für eine Rückzahlung erfasse eine Zahlung mit negativem Betrag.
                </span>
              </div>
            </div>
          )}

          {detail.payments.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">Noch keine Zahlungen.</p>
          ) : (
            <table className="mt-3 w-full text-sm">
              <thead className="text-slate-500">
                <tr className="border-b border-slate-100">
                  <th className="py-2 text-left font-medium">Datum</th>
                  <th className="py-2 text-left font-medium">Zahlungsart</th>
                  <th className="py-2 text-right font-medium">Betrag</th>
                  <th className="py-2 text-left font-medium">Notiz</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {detail.payments.map((p) => (
                  <tr key={p.id} className="border-b border-slate-50">
                    <td className="whitespace-nowrap py-2">{formatIsoDate(p.paid_on)}</td>
                    <td className="py-2">{PAYMENT_METHOD_LABELS[p.method]}</td>
                    <td
                      className={
                        "whitespace-nowrap py-2 text-right " +
                        (p.amount_rappen < 0 ? "text-red-600" : "")
                      }
                    >
                      {formatCHF(p.amount_rappen)}
                    </td>
                    <td className="py-2 text-slate-500">{p.note ?? "—"}</td>
                    <td className="whitespace-nowrap py-2 text-right">
                      {p.method !== "credit" && (
                        <>
                          <button
                            onClick={() => setEditingPayment(p)}
                            className="text-slate-600 hover:text-slate-900"
                          >
                            Bearbeiten
                          </button>
                          <button
                            onClick={() => deletePayment(p)}
                            className="ml-3 text-slate-400 hover:text-red-600"
                          >
                            Löschen
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <p className="mt-3 text-sm font-medium">
            {inv.balance_rappen > 0 ? (
              <span className="text-amber-700">Rest {formatCHF(inv.balance_rappen)}</span>
            ) : inv.balance_rappen < 0 ? (
              <span className="text-violet-700">Überzahlung {formatCHF(-inv.balance_rappen)}</span>
            ) : inv.paid_rappen !== 0 ? (
              <span className="text-emerald-700">Vollständig bezahlt</span>
            ) : null}
          </p>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="text-sm font-semibold">Aktionen</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => openPdf("auto")} className="btn-sm">
            PDF öffnen
          </button>
          {isCancelled && (
            <button onClick={() => openPdf("original")} className="btn-sm">
              Original-PDF
            </button>
          )}
          {!isStorno && !isCancelled && (
            <>
              <button
                type="button"
                disabled
                title="E-Mail-Versand kommt bald"
                className="btn-sm cursor-not-allowed opacity-50"
              >
                Als E-Mail senden
              </button>
              <button onClick={() => run(() => markSent("post"))} className="btn-sm">
                Als versendet markieren (Post)
              </button>
            </>
          )}
          {canRemind && (
            <>
              <button onClick={() => setShowReminder(true)} className="btn-sm">
                Erinnerung erstellen
              </button>
              <button onClick={() => setShowMahnpause(true)} className="btn-sm">
                {inv.mahnpause_until ? "Zahlungspause ändern…" : "Zahlungspause bis…"}
              </button>
            </>
          )}
          {canRemind && inv.written_off_at == null && (
            <button onClick={() => setShowWriteOff(true)} className="btn-sm">
              Abschreiben
            </button>
          )}
          {inv.written_off_at != null && (
            <button
              onClick={() =>
                run(async () => {
                  await mutate(`/tenants/${tenantId}/invoices/${inv.id}/clear-write-off`, {});
                  onReload();
                })
              }
              className="btn-sm"
            >
              Abschreibung aufheben
            </button>
          )}
          {canCorrect && (
            <>
              <button onClick={() => setShowReduce(true)} className="btn-sm">
                Betrag reduzieren
              </button>
              <button
                onClick={() => setShowCancel(true)}
                className="btn-sm text-red-600 hover:bg-red-50"
              >
                Stornieren
              </button>
            </>
          )}
          {!isStorno && (
            <button
              onClick={() =>
                run(async () => {
                  const res = await mutate<{ invoice_id: string }>(
                    `/tenants/${tenantId}/invoices/${inv.id}/duplicate`,
                    {},
                  );
                  onNavigate(res.invoice_id);
                })
              }
              className="btn-sm"
            >
              Duplizieren
            </button>
          )}
          {isCancelled && (
            <button
              onClick={() =>
                run(async () => {
                  const res = await mutate<{ invoice_id: string }>(
                    `/tenants/${tenantId}/invoices/${inv.id}/restore-cancelled`,
                    {},
                  );
                  onNavigate(res.invoice_id);
                })
              }
              className="btn-sm"
            >
              Als neue Rechnung wiederherstellen
            </button>
          )}
        </div>
      </div>

      {addingPayment && (
        <PaymentFormModal
          title="Zahlung erfassen"
          initial={{
            amount_rappen: inv.balance_rappen > 0 ? inv.balance_rappen : 0,
            paid_on: todayIso(),
            method: "bank",
            note: null,
          }}
          submitLabel="Zahlung erfassen"
          onClose={() => setAddingPayment(false)}
          onSubmit={async (v) => {
            await mutate(`/tenants/${tenantId}/invoices/${inv.id}/payments`, v);
            setAddingPayment(false);
            onReload();
          }}
        />
      )}

      {editingPayment && (
        <PaymentFormModal
          title="Zahlung bearbeiten"
          initial={{
            amount_rappen: editingPayment.amount_rappen,
            paid_on: editingPayment.paid_on,
            method: editingPayment.method,
            note: editingPayment.note,
          }}
          submitLabel="Speichern"
          onClose={() => setEditingPayment(null)}
          onSubmit={async (v) => {
            await mutate(`/tenants/${tenantId}/payments/${editingPayment.id}`, v, "PATCH");
            setEditingPayment(null);
            onReload();
          }}
        />
      )}

      {showReminder && (
        <ReminderModal
          tenantId={tenantId}
          invoiceId={inv.id}
          nextNumber={inv.reminder_count + 1}
          onClose={() => setShowReminder(false)}
          onDone={() => {
            setShowReminder(false);
            onReload();
          }}
        />
      )}

      {showMahnpause && (
        <MahnpauseModal
          tenantId={tenantId}
          invoiceId={inv.id}
          current={inv.mahnpause_until}
          onClose={() => setShowMahnpause(false)}
          onDone={() => {
            setShowMahnpause(false);
            onReload();
          }}
        />
      )}

      {showWriteOff && (
        <WriteOffModal
          tenantId={tenantId}
          invoiceId={inv.id}
          balanceRappen={inv.balance_rappen}
          onClose={() => setShowWriteOff(false)}
          onDone={() => {
            setShowWriteOff(false);
            onReload();
          }}
        />
      )}

      {showReduce && (
        <ReduceModal
          tenantId={tenantId}
          invoiceId={inv.id}
          totalRappen={inv.total_rappen}
          onClose={() => setShowReduce(false)}
          onDone={(replacementId) => {
            setShowReduce(false);
            if (replacementId) onNavigate(replacementId);
            else onReload();
          }}
        />
      )}

      {showCancel && (
        <CancelModal
          tenantId={tenantId}
          invoiceId={inv.id}
          hasCoverage={inv.coverage.length > 0}
          onClose={() => setShowCancel(false)}
          onDone={(replacementId) => {
            setShowCancel(false);
            if (replacementId) onNavigate(replacementId);
            else onReload();
          }}
        />
      )}
    </div>
  );
}

function Banner({ tone, children }: { tone: "red" | "violet" | "slate"; children: ReactNode }) {
  const styles =
    tone === "red"
      ? "border-red-200 bg-red-50 text-red-700"
      : tone === "violet"
        ? "border-violet-200 bg-violet-50 text-violet-700"
        : "border-slate-200 bg-slate-50 text-slate-700";
  return <div className={`rounded-lg border px-4 py-2.5 text-sm ${styles}`}>{children}</div>;
}

function ChainLink({
  target,
  onNavigate,
}: {
  target: { id: string; number: string | null };
  onNavigate: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onNavigate(target.id)}
      className="font-medium underline hover:no-underline"
    >
      {target.number ?? "Entwurf"}
    </button>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <h3 className="mb-3 text-sm font-semibold">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// --- Action modals -----------------------------------------------------------------

function ReminderModal({
  tenantId,
  invoiceId,
  nextNumber,
  onClose,
  onDone,
}: {
  tenantId: string;
  invoiceId: string;
  nextNumber: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [date, setDate] = useState(isoPlusDays(14));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await mutate(`/tenants/${tenantId}/invoices/${invoiceId}/reminder`, {
        new_due_date: date,
      });
      window.open(
        apiUrl(`/tenants/${tenantId}/invoices/${invoiceId}/reminder/${nextNumber}/pdf`),
        "_blank",
        "noopener,noreferrer",
      );
      onDone();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Modal title="Erinnerung erstellen" onClose={onClose}>
      <p className="text-sm text-slate-600">
        Erstellt ein Erinnerungs-PDF ({nextNumber}. Erinnerung) mit neuem Zahlungsziel.
      </p>
      <label className="mt-4 block">
        <span className="text-xs font-medium text-slate-500">Neues Zahlungsziel</span>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="input mt-1"
        />
      </label>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="btn-sm px-3 py-1.5 text-sm">
          Abbrechen
        </button>
        <button
          disabled={busy || !date}
          onClick={submit}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? "Erstellen…" : "Erstellen & PDF öffnen"}
        </button>
      </div>
    </Modal>
  );
}

function MahnpauseModal({
  tenantId,
  invoiceId,
  current,
  onClose,
  onDone,
}: {
  tenantId: string;
  invoiceId: string;
  current: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [date, setDate] = useState(current ?? isoPlusDays(30));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(until: string | null) {
    setBusy(true);
    setError(null);
    try {
      await mutate(`/tenants/${tenantId}/invoices/${invoiceId}/mahnpause`, { until });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Modal title="Zahlungspause" onClose={onClose}>
      <p className="text-sm text-slate-600">
        Bis zu diesem Datum gilt die Rechnung nicht als überfällig und es werden keine Erinnerungen
        vorgeschlagen.
      </p>
      <label className="mt-4 block">
        <span className="text-xs font-medium text-slate-500">Pause bis</span>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="input mt-1"
        />
      </label>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        {current && (
          <button
            disabled={busy}
            onClick={() => submit(null)}
            className="btn-sm px-3 py-1.5 text-sm text-red-600"
          >
            Pause aufheben
          </button>
        )}
        <button onClick={onClose} className="btn-sm px-3 py-1.5 text-sm">
          Abbrechen
        </button>
        <button
          disabled={busy || !date}
          onClick={() => submit(date)}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? "Speichern…" : "Speichern"}
        </button>
      </div>
    </Modal>
  );
}

function WriteOffModal({
  tenantId,
  invoiceId,
  balanceRappen,
  onClose,
  onDone,
}: {
  tenantId: string;
  invoiceId: string;
  balanceRappen: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await mutate(`/tenants/${tenantId}/invoices/${invoiceId}/write-off`, {
        note: note.trim(),
      });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Modal title="Rechnung abschreiben" onClose={onClose}>
      <p className="text-sm text-slate-600">
        Der offene Betrag von <span className="font-medium">{formatCHF(balanceRappen)}</span> wird
        als uneinbringlich abgeschrieben. Das lässt sich jederzeit wieder aufheben.
      </p>
      <label className="mt-4 block">
        <span className="text-xs font-medium text-slate-500">Begründung (Pflicht)</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className="input mt-1"
          autoFocus
        />
      </label>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="btn-sm px-3 py-1.5 text-sm">
          Abbrechen
        </button>
        <button
          disabled={busy || note.trim().length === 0}
          onClick={submit}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? "Abschreiben…" : "Abschreiben"}
        </button>
      </div>
    </Modal>
  );
}

function ReduceModal({
  tenantId,
  invoiceId,
  totalRappen,
  onClose,
  onDone,
}: {
  tenantId: string;
  invoiceId: string;
  totalRappen: number;
  onClose: () => void;
  onDone: (replacementDraftId: string | null) => void;
}) {
  const [amount, setAmount] = useState(rappenToChfInput(totalRappen));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = parseChfToRappen(amount);
  const valid = parsed !== null && parsed >= 0 && parsed !== totalRappen;

  async function submit() {
    if (!valid || parsed === null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await mutate<{ storno_id: string; replacement_draft_id: string | null }>(
        `/tenants/${tenantId}/invoices/${invoiceId}/reduce-amount`,
        { new_total_rappen: parsed, reason: reason.trim() },
      );
      onDone(res.replacement_draft_id);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Modal title="Betrag reduzieren" onClose={onClose}>
      <p className="text-sm text-slate-600">
        Die Rechnung über <span className="font-medium">{formatCHF(totalRappen)}</span> wird
        storniert und durch einen neuen Entwurf mit dem reduzierten Betrag ersetzt.
      </p>
      <label className="mt-4 block">
        <span className="text-xs font-medium text-slate-500">Neuer Totalbetrag (CHF)</span>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          className={"input mt-1 " + (parsed === null ? "border-red-300" : "")}
          autoFocus
        />
      </label>
      <label className="mt-3 block">
        <span className="text-xs font-medium text-slate-500">Grund</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className="input mt-1"
        />
      </label>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="btn-sm px-3 py-1.5 text-sm">
          Abbrechen
        </button>
        <button
          disabled={busy || !valid}
          onClick={submit}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? "Reduzieren…" : "Reduzieren"}
        </button>
      </div>
    </Modal>
  );
}

function CancelModal({
  tenantId,
  invoiceId,
  hasCoverage,
  onClose,
  onDone,
}: {
  tenantId: string;
  invoiceId: string;
  hasCoverage: boolean;
  onClose: () => void;
  onDone: (replacementDraftId: string | null) => void;
}) {
  const [reason, setReason] = useState("");
  const [decision, setDecision] = useState<"replacement" | "skip" | "rebill">("replacement");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await mutate<{ storno_id: string; replacement_draft_id: string | null }>(
        `/tenants/${tenantId}/invoices/${invoiceId}/cancel`,
        { reason: reason.trim(), period_decision: decision },
      );
      onDone(res.replacement_draft_id);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  const options: { key: "replacement" | "skip" | "rebill"; label: string; hint: string }[] = [
    {
      key: "replacement",
      label: "Korrigierte Rechnung als Entwurf erstellen",
      hint: "Empfohlen — du passt den Entwurf an und gibst ihn neu frei.",
    },
    {
      key: "skip",
      label: "Periode überspringen — keine neue Rechnung",
      hint: "Die abgedeckte Periode bleibt unverrechnet.",
    },
    {
      key: "rebill",
      label: "Periode neu verrechnen lassen",
      hint: "Beim nächsten Rechnungslauf entsteht automatisch ein neuer Entwurf.",
    },
  ];

  return (
    <Modal title="Rechnung stornieren" onClose={onClose}>
      <p className="text-sm text-slate-600">
        Es wird eine Stornorechnung erstellt. Die Originalrechnung bleibt als Beleg erhalten.
      </p>
      <label className="mt-4 block">
        <span className="text-xs font-medium text-slate-500">Grund</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className="input mt-1"
          autoFocus
        />
      </label>
      {hasCoverage && (
        <div className="mt-4 space-y-2 text-sm">
          {options.map((o) => (
            <label key={o.key} className="flex items-start gap-2">
              <input
                type="radio"
                checked={decision === o.key}
                onChange={() => setDecision(o.key)}
                className="mt-0.5 accent-slate-900"
              />
              <span>
                <span className="font-medium">{o.label}</span>
                <span className="block text-xs text-slate-500">{o.hint}</span>
              </span>
            </label>
          ))}
        </div>
      )}
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="btn-sm px-3 py-1.5 text-sm">
          Abbrechen
        </button>
        <button
          disabled={busy}
          onClick={submit}
          className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
        >
          {busy ? "Stornieren…" : "Stornieren"}
        </button>
      </div>
    </Modal>
  );
}

// --- Verlauf (audit trail) ----------------------------------------------------------

const AUDIT_LABELS: Record<string, string> = {
  "invoice.draft_created": "Entwurf erstellt",
  "invoice.draft_edited": "Entwurf bearbeitet",
  "invoice.draft_deleted": "Entwurf gelöscht",
  "invoice.draft_retracted": "Entwurf zurückgezogen",
  "invoice.issue": "Freigegeben",
  "invoice.cancel": "Storniert",
  "invoice.restore_cancelled": "Als neue Rechnung wiederhergestellt",
  "invoice.reduce_amount": "Betrag reduziert",
  "invoice.duplicate": "Dupliziert",
  "invoice.merge": "Zusammengeführt",
  "invoice.attach_coverage": "Periode hinzugefügt",
  "invoice.detach_coverage": "Periode entfernt",
  "invoice.write_off": "Abgeschrieben",
  "invoice.clear_write_off": "Abschreibung aufgehoben",
  "invoice.mahnpause": "Zahlungspause gesetzt",
  "invoice.mark_sent": "Als versendet markiert",
  "invoice.reminder": "Erinnerung erstellt",
  "payment.record": "Zahlung erfasst",
  "payment.update": "Zahlung bearbeitet",
  "payment.delete": "Zahlung gelöscht",
  "payment.reassign": "Zahlung umgebucht",
  "payment.forwarded": "Zahlung übertragen",
};

function AuditSection({ tenantId, invoiceId }: { tenantId: string; invoiceId: string }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);

  useEffect(() => {
    if (!open || entries !== null) return;
    api<{ entries: AuditEntry[] }>(`/tenants/${tenantId}/invoices/${invoiceId}/audit`)
      .then((r) => setEntries(r.entries))
      .catch(() => setEntries([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setEntries(null);
    setOpen(false);
  }, [tenantId, invoiceId]);

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-6 py-4 text-sm font-semibold"
      >
        Verlauf
        <span className="text-slate-400">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-6 py-4">
          {entries === null ? (
            <p className="text-sm text-slate-400">Lädt…</p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-slate-400">Keine Einträge.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {entries.map((e) => (
                <li key={e.id} className="flex flex-wrap items-baseline gap-x-3">
                  <span className="whitespace-nowrap text-xs text-slate-400">
                    {formatDate(e.at)}
                  </span>
                  <span>{AUDIT_LABELS[e.action] ?? e.action}</span>
                  <span className="text-xs text-slate-400">{e.actor}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
